import { defineStore } from "pinia";
import { useLocalStorage } from "@vueuse/core";
import { reactive } from "vue";

const JOIN_SYMBOL = "/";

function flattenObject(obj, prefix = "") {
  const result = {};
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      const newKey = prefix ? `${prefix}${JOIN_SYMBOL}${key}` : key;
      if (typeof obj[key] === "object" && obj[key] !== null && !Array.isArray(obj[key])) {
        if ("value" in obj[key]) {
          result[newKey] = obj[key].value;
        } else {
          Object.assign(result, flattenObject(obj[key], newKey));
        }
      } else {
        result[newKey] = obj[key];
      }
    }
  }
  return result;
}

function createReactiveProxy(target, baseKey, state, triggerRef) {
  return new Proxy(target, {
    get(target, key) {
      if (typeof key === "symbol" || key.toString().startsWith("__v_")) {
        return target[key];
      }
      const fullKey = baseKey ? `${baseKey}${JOIN_SYMBOL}${key}` : key;
      if (fullKey in state) {
        return state[fullKey].value;
      }
      if (typeof target[key] === "object" && target[key] !== null) {
        return createReactiveProxy(target[key], fullKey, state, triggerRef);
      }
      return undefined;
    },
    set(_target, key, value) {
      const fullKey = baseKey ? `${baseKey}${JOIN_SYMBOL}${key}` : key;
      // fullKey can already be a registered leaf even when `value` is itself a plain object —
      // e.g. explore.autoArrangeByScope is declared in persistentConfig.json as a single
      // `{ value: {} }` leaf holding a whole `{ [scopeKey]: boolean }` map, written wholesale
      // by setScopeAutoArrange. Fanning that out into per-scopeKey child leaves (the branch
      // below) would silently create orphaned keys nobody ever reads — get() finds fullKey in
      // state first and returns the untouched original leaf, so the write is permanently lost.
      // A leaf write must always go straight to state[fullKey], regardless of value's type.
      if (fullKey in state) {
        state[fullKey].value = value;
        triggerRef();
        return true;
      }
      // A plain-object value where fullKey is NOT yet a leaf (e.g. `Object.assign(proxy.exportSettings,
      // {...})` from a cloneDeep of the whole parent) must fan out to each leaf under fullKey,
      // matching how the store was originally flattened by generateStoreStructure. Storing it as
      // one opaque state[fullKey] entry instead would permanently shadow the leaves: once `fullKey
      // in state` is true, get() stops recursing into the nested proxy and returns this stale
      // snapshot for every future read of fullKey and its former children.
      if (typeof value === "object" && value !== null && !Array.isArray(value)) {
        const flat = flattenObject(value, fullKey);
        for (const [leafKey, leafValue] of Object.entries(flat)) {
          if (!(leafKey in state)) {
            state[leafKey] = useLocalStorage(leafKey, leafValue);
          } else {
            state[leafKey].value = leafValue;
          }
        }
        triggerRef();
        return true;
      }
      state[fullKey] = useLocalStorage(fullKey, value);
      triggerRef();
      return true;
    },
  });
}

function generateStoreStructure(config) {
  const flatConfig = flattenObject(config);
  const state = {};

  Object.entries(flatConfig).forEach(([key, value]) => {
    state[key] = useLocalStorage(key, value);
  });

  return state;
}

export function createPersistentStore(config) {
  const state = generateStoreStructure(config);

  return defineStore("persistentStorage", () => {
    const storeObject = createReactiveProxy(config, "", state, () => {});

    const store = reactive(storeObject);

    return {
      ...store,
      getValue: (path) => {
        const keys = Array.isArray(path) ? path : path.split(JOIN_SYMBOL);
        let current = store;
        for (let i = 0; i < keys.length; i++) {
          if (current[keys[i]] === undefined) {
            return undefined;
          }
          current = current[keys[i]];
        }
        return current;
      },
      setValue: (path, value) => {
        const keys = Array.isArray(path) ? path : path.split(JOIN_SYMBOL);
        let current = store;
        for (let i = 0; i < keys.length - 1; i++) {
          if (!(keys[i] in current)) {
            current[keys[i]] = {};
          }
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
      },
    };
  });
}

export function initializeStore(store, config) {
  const flatConfig = flattenObject(config);
  Object.entries(flatConfig).forEach(([key, value]) => {
    if (store.getValue(key) === undefined) {
      store.setValue(key, value);
    }
  });
}
